import { useCallback, useEffect, useRef, useState, type RefCallback } from 'react';

const ROOT_MARGIN_PX = '80px';

export const useRenderWhenVisible = (renderImmediately: boolean): [RefCallback<HTMLElement>, boolean] => {
    const [isVisible, setIsVisible] = useState(renderImmediately);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const isVisibleRef = useRef(isVisible);

    isVisibleRef.current = isVisible;

    const disconnectObserver = (): void => {
        if (observerRef.current !== null) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
    };

    useEffect(() => {
        if (renderImmediately) {
            setIsVisible(true);
            disconnectObserver();
        }
    }, [renderImmediately]);

    useEffect(() => {
        return disconnectObserver;
    }, []);

    const setRef = useCallback(
        (element: HTMLElement | null): void => {
            disconnectObserver();

            if (element === null || isVisibleRef.current || renderImmediately) {
                return;
            }

            const observer = new IntersectionObserver(
                (entries) => {
                    for (const entry of entries) {
                        if (entry.isIntersecting) {
                            setIsVisible(true);
                            disconnectObserver();
                        }
                    }
                },
                { rootMargin: ROOT_MARGIN_PX, threshold: 0 }
            );

            observer.observe(element);
            observerRef.current = observer;
        },
        [renderImmediately]
    );

    return [setRef, isVisible || renderImmediately];
};
